<template>
    <v-row :key="updateKey">
        <v-col cols="12">
            <h2 class="mt-5 mb-4">{{ title }}</h2>

            <v-text-field
                v-model="details.email"
                :disabled="index == 0"
                label="Email"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.email"
                :error="getError(`applicants.${index}.email`)"
                :error-messages="getErrorMessages(`applicants.${index}.email`)"
                @input="removeErrorMessage(`applicants.${index}.email`)"
            ></v-text-field>

            <v-select
                v-model="details.title"
                :items="titleChoices"
                label="Title"
                required
                outlined
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.title"
                :error="getError(`applicants.${index}.title`)"
                :error-messages="getErrorMessages(`applicants.${index}.title`)"
                @input="removeErrorMessage(`applicants.${index}.title`)"
            ></v-select>

            <v-text-field
                v-model="details.firstName"
                label="First name"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.firstName"
                :error="getError(`applicants.${index}.firstName`)"
                :error-messages="getErrorMessages(`applicants.${index}.firstName`)"
                @input="removeErrorMessage(`applicants.${index}.firstName`)"
            ></v-text-field>

            <v-text-field
                v-model="details.middleNames"
                label="Middle name(s)"
                outlined
                hide-details="auto"
                class="rounded-0 mb-4"
                :error="getError(`applicants.${index}.middleNames`)"
                :error-messages="getErrorMessages(`applicants.${index}.middleNames`)"
                @input="removeErrorMessage(`applicants.${index}.middleNames`)"
            ></v-text-field>

            <v-text-field
                v-model="details.lastName"
                label="Last name"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.lastName"
                :error="getError(`applicants.${index}.lastName`)"
                :error-messages="getErrorMessages(`applicants.${index}.lastName`)"
                @input="removeErrorMessage(`applicants.${index}.lastName`)"
            ></v-text-field>

            <v-text-field
                v-model="details.previousSurname"
                label="Previous surname"
                outlined
                hide-details="auto"
                class="rounded-0 mb-4"
                :rules="rules.previousSurname"
                :error="getError(`applicants.${index}.previousSurname`)"
                :error-messages="getErrorMessages(`applicants.${index}.previousSurname`)"
                @input="removeErrorMessage(`applicants.${index}.previousSurname`)"
            ></v-text-field>

            <v-menu
                ref="dateOfBirthMenuRef"
                v-model="showDateOfBirthDatePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="dateOfBirthFormatted"
                        label="Date of birth"
                        append-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        required
                        hide-details="auto"
                        class="rounded-0 mb-4 v-input--required"
                        :error="getError(`applicants.${index}.dateOfBirth`)"
                        :error-messages="getErrorMessages(`applicants.${index}.dateOfBirth`)"
                        @input="removeErrorMessage(`applicants.${index}.dateOfBirth`)"
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="details.dateOfBirth"
                    :max="(new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)"
                    :active-picker.sync="dateOfBirthActivePicker"
                    min="1900-01-01"
                    @change="saveDateOfBirthPicker"
                ></v-date-picker>
            </v-menu>

            <v-select
                v-model="details.maritalStatus"
                :items="maritalStatusChoices"
                item-text="name"
                item-value="id"
                label="Marital Status"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.maritalStatus"
                :error="getError(`applicants.${index}.maritalStatus`)"
                :error-messages="getErrorMessages(`applicants.${index}.maritalStatus`)"
                @input="removeErrorMessage(`applicants.${index}.maritalStatus`)"
            ></v-select>

            <v-select
                v-if="index > 0"
                v-model="details.relationshipToPrimary"
                :items="relationshipToPrimaryChoices"
                label="Relationship to primary"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.relationshipToPrimary"
                :error="getError(`applicants.${index}.relationshipToPrimary`)"
                :error-messages="getErrorMessages(`applicants.${index}.relationshipToPrimary`)"
                @input="removeErrorMessage(`applicants.${index}.relationshipToPrimary`)"
            ></v-select>

            <v-select
                v-model="details.gender"
                :items="genderChoices"
                item-text="name"
                item-value="id"
                label="Gender"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.gender"
                :error="getError(`applicants.${index}.gender`)"
                :error-messages="getErrorMessages(`applicants.${index}.gender`)"
                @input="removeErrorMessage(`applicants.${index}.gender`)"
            ></v-select>

            <legend class="v-label v-label--required theme--light mt-2">
                Are you current or ex-serving MoD personnel?
            </legend>
            <v-radio-group
                class="mt-1"
                v-model.number="details.MODWorker"
                row
                :error="getError(`applicants.${index}.MODWorker`)"
                :error-messages="getErrorMessages(`applicants.${index}.MODWorker`)"
                @input="removeErrorMessage(`applicants.${index}.MODWorker`)"
            >
                <v-radio
                    label="Yes"
                    :value="true"
                ></v-radio>
                <v-radio
                    label="No"
                    :value="false"
                ></v-radio>
            </v-radio-group>

            <h3 class="mt-10 mb-4">Contact Details</h3>

            <v-text-field
                v-model="details.phone"
                label="Phone number"
                type="tel"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.phone"
                :error="getError(`applicants.${index}.phone`)"
                :error-messages="getErrorMessages(`applicants.${index}.phone`)"
                @input="removeErrorMessage(`applicants.${index}.phone`)"
            ></v-text-field>

            <v-text-field
                v-model="details.homePhone"
                label="Home phone number"
                outlined
                hide-details="auto"
                class="rounded-0 mb-4"
                :rules="rules.homePhone"
                :error="getError(`applicants.${index}.homePhone`)"
                :error-messages="getErrorMessages(`applicants.${index}.homePhone`)"
                @input="removeErrorMessage(`applicants.${index}.homePhone`)"
            ></v-text-field>

            <v-text-field
                v-model="details.workPhone"
                label="Work phone number"
                outlined
                hide-details="auto"
                class="rounded-0 mb-4"
                :rules="rules.workPhone"
                :error="getError(`applicants.${index}.workPhone`)"
                :error-messages="getErrorMessages(`applicants.${index}.workPhone`)"
                @input="removeErrorMessage(`applicants.${index}.workPhone`)"
            ></v-text-field>

            <v-text-field
                v-model="details.mobilePhone"
                label="Mobile phone number"
                outlined
                hide-details="auto"
                class="rounded-0 mb-4"
                :rules="rules.mobilePhone"
                :error="getError(`applicants.${index}.mobilePhone`)"
                :error-messages="getErrorMessages(`applicants.${index}.mobilePhone`)"
                @input="removeErrorMessage(`applicants.${index}.mobilePhone`)"
            ></v-text-field>

            <!--
            <v-text-field
                v-model="details.nationalInsuranceNumber"
                label="National Insurance number"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.nationalInsuranceNumber"
                :error="getError(`applicants.${index}.nationalInsuranceNumber`)"
                :error-messages="getErrorMessages(`applicants.${index}.nationalInsuranceNumber`)"
                @input="removeErrorMessage(`applicants.${index}.nationalInsuranceNumber`)"
            ></v-text-field>-->

            <h3 class="mt-10 mb-4">Current Address</h3>

            <v-select
                v-model="details.livingCircumstance"
                :items="livingCircumstanceChoices"
                item-text="name"
                item-value="id"
                label="Current accommodation"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.livingCircumstance"
                :error="getError(`applicants.${index}.livingCircumstance`)"
                :error-messages="getErrorMessages(`applicants.${index}.livingCircumstance`)"
                @input="removeErrorMessage(`applicants.${index}.livingCircumstance`)"
            ></v-select>

            <v-checkbox
                v-model="details.addressSameAsMainApplicant"
                label="Same as primary applicant's address"
                class="rounded-0 mb-4"
                hide-details="auto"
                @change="setApplicantCurrentAddressFromMain"
                v-if="index > 0"
            ></v-checkbox>

            <v-text-field
                v-model="details.homeAddressLine1"
                label="House name or number"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :disabled="details.addressSameAsMainApplicant"
                :rules="rules.homeAddressLine1"
                :error="getError(`applicants.${index}.homeAddressLine1`)"
                :error-messages="getErrorMessages(`applicants.${index}.homeAddressLine1`)"
                @input="removeErrorMessage(`applicants.${index}.homeAddressLine1`)"
            ></v-text-field>

            <v-text-field
                v-model="details.homeAddressLine2"
                label="Street address"
                required
                outlined
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :disabled="details.addressSameAsMainApplicant"
                :error="getError(`applicants.${index}.homeAddressLine2`)"
                :error-messages="getErrorMessages(`applicants.${index}.homeAddressLine2`)"
                @input="removeErrorMessage(`applicants.${index}.homeAddressLine2`)"
            ></v-text-field>

            <div class="d-flex">
                <v-text-field
                    v-model="details.homePostcode"
                    label="Postcode"
                    outlined
                    required
                    hide-details="auto"
                    class="rounded-0 mb-4 mr-4 v-input--required"
                    :disabled="details.addressSameAsMainApplicant"
                    :rules="rules.homePostcode"
                    :error="getError(`applicants.${index}.homePostcode`)"
                    :error-messages="getErrorMessages(`applicants.${index}.homePostcode`)"
                    @input="removeErrorMessage(`applicants.${index}.homePostcode`)"
                >
                    <template v-slot:append>
                        <v-menu
                            open-on-hover
                            origin="center center"
                            transition="scale-transition"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-help-box</v-icon>
                                </v-btn>
                            </template>

                            <v-card
                                elevation="2"
                                max-width="260"
                            >
                                <v-card-text>
                                    Tell us the postcode for where you live at the moment.
                                </v-card-text>
                            </v-card>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-btn
                    @click="searchPostcode('homePostcode')"
                    small
                    color="primary"
                    class="mt-4"
                    v-if="!formDisabled"
                >
                    Search
                </v-btn>
            </div>

            <v-select
                v-model="details.homeLocalAuthorityId"
                :items="localAuthorityChoices"
                label="Local authority"
                required
                outlined
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                item-text="name"
                item-value="id"
                :disabled="details.addressSameAsMainApplicant"
                :rules="rules.homeLocalAuthority"
                :error="getError(`applicants.${index}.homeLocalAuthorityId`)"
                :error-messages="getErrorMessages(`applicants.${index}.homeLocalAuthorityId`)"
                @input="removeErrorMessage(`applicants.${index}.homeLocalAuthorityId`)"
            ></v-select>

            <v-text-field
                v-model="details.homeTown"
                label="Town"
                outlined
                hide-details="auto"
                loading="true"
                class="rounded-0 mb-4"
                :disabled="details.addressSameAsMainApplicant"
                :error="getError(`applicants.${index}.homeTown`)"
                :error-messages="getErrorMessages(`applicants.${index}.homeTown`)"
                @input="removeErrorMessage(`applicants.${index}.homeTown`)"
            ></v-text-field>

            <v-select
                v-model="details.homeCounty"
                :items="countyChoices"
                label="County"
                outlined
                hide-details="auto"
                class="rounded-0 mb-4"
                item-text="name"
                item-value="id"
                :disabled="details.addressSameAsMainApplicant"
                :error="getError(`applicants.${index}.homeCounty`)"
                :error-messages="getErrorMessages(`applicants.${index}.homeCounty`)"
                @input="removeErrorMessage(`applicants.${index}.homeCounty`)"
            ></v-select>

            <v-menu
                ref="homeMoveInDateMenuRef"
                v-model="showHomeMoveInDatePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="homeMoveInDateFormatted"
                        label="When did you move to this address?"
                        append-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        required
                        hide-details="auto"
                        class="rounded-0 mb-4 v-input--required"
                        :rules="rules.homeMoveInDate"
                        :error="getError(`applicants.${index}.homeMoveInDate`)"
                        :error-messages="getErrorMessages(`applicants.${index}.homeMoveInDate`)"
                        @input="removeErrorMessage(`applicants.${index}.homeMoveInDate`)"
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="details.homeMoveInDate"
                    :max="(new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)"
                    min="1900-01-01"
                    :active-picker.sync="homeMoveInActivePicker"
                    @change="saveMoveInDatePicker"
                ></v-date-picker>
            </v-menu>

            <h3 class="mt-10 mb-4">Previous Addresses</h3>

            <div :key="addressIndex" v-for="(previousAddress, addressIndex) in details.previousAddresses">
                <v-row class="mb-3 mt-2">
                    <v-col cols="6">
                        <h4 class="mt-1">Previous Address {{ addressIndex+1 }}</h4>
                    </v-col>

                    <v-col cols="6" align="end">
                        <v-btn
                            color="primary"
                            outlined
                            small
                            @click.prevent="removePreviousAddress(addressIndex)"
                            v-if="!formDisabled"
                        >
                            Remove Previous Address
                        </v-btn>
                    </v-col>
                </v-row>

                <v-text-field
                    v-model="details.previousAddresses[addressIndex]['addressLine1']"
                    label="House name or number"
                    outlined
                    hide-details="auto"
                    class="rounded-0 mb-4"
                    :rules="rules.previousBuildingName"
                    :error="getError(`applicants.${index}.previousAddresses.${addressIndex}.addressLine1`)"
                    :error-messages="getErrorMessages(`applicants.${index}.previousAddresses.${addressIndex}.addressLine1`)"
                ></v-text-field>

                <v-text-field
                    v-model="details.previousAddresses[addressIndex]['addressLine2']"
                    label="Street address"
                    outlined
                    hide-details="auto"
                    class="rounded-0 mb-4"
                    :rules="rules.previousStreetName"
                    :error="getError(`applicants.${index}.previousAddresses.${addressIndex}.addressLine2`)"
                    :error-messages="getErrorMessages(`applicants.${index}.previousAddresses.${addressIndex}.addressLine2`)"
                ></v-text-field>

                <div class="d-flex">
                    <v-text-field
                        v-model="details.previousAddresses[addressIndex]['postcode']"
                        label="Postcode"
                        outlined
                        hide-details="auto"
                        class="rounded-0 mb-4 mr-4"
                        :rules="rules.previousPostcode"
                        :error="getError(`applicants.${index}.previousAddresses.${addressIndex}.postcode`)"
                        :error-messages="getErrorMessages(`applicants.${index}.previousAddresses.${addressIndex}.postcode`)"
                    ></v-text-field>
                    <v-btn
                        @click="searchPostcode('postcode', addressIndex)"
                        small
                        color="primary"
                        class="mt-4"
                        v-if="!formDisabled"
                    >
                        Search
                    </v-btn>
                </div>

                <v-text-field
                    v-model="details.previousAddresses[addressIndex]['town']"
                    label="Town"
                    outlined
                    hide-details="auto"
                    class="rounded-0 mb-4"
                    :error="getError(`applicants.${index}.previousAddresses.${addressIndex}.town`)"
                    :error-messages="getErrorMessages(`applicants.${index}.previousAddresses.${addressIndex}.town`)"
                ></v-text-field>

                <v-select
                    v-model="details.previousAddresses[addressIndex]['localAuthorityId']"
                    :items="localAuthorityChoices"
                    label="Local authority"
                    outlined
                    hide-details="auto"
                    class="rounded-0 mb-4"
                    item-text="name"
                    item-value="id"
                    :error="getError(`applicants.${index}.previousAddresses.localAuthorityId`)"
                    :error-messages="getErrorMessages(`applicants.${index}.previousAddresses.localAuthorityId`)"
                    @input="removeErrorMessage(`applicants.${index}.previousAddresses.localAuthorityId`)"
                ></v-select>

                <v-select
                    v-model="details.previousAddresses[addressIndex]['county']"
                    :items="countyChoices"
                    label="County"
                    outlined
                    hide-details="auto"
                    class="rounded-0 mb-4"
                    item-text="name"
                    item-value="id"
                    :error="getError(`applicants.${index}.previousAddresses.${addressIndex}.county`)"
                    :error-messages="getErrorMessages(`applicants.${index}.previousAddresses.${addressIndex}.county`)"
                    @input="removeErrorMessage(`applicants.${index}.previousAddresses.${addressIndex}.county`)"
                ></v-select>

            <v-menu
                ref="previousAddressMoveInDateMenuRef"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        :value="previousAddressFormattedMoveInDate(details.previousAddresses[addressIndex]['dateMovedIn'])"
                        label="When did you move to this address?"
                        append-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        required
                        hide-details="auto"
                        class="rounded-0 mb-4 v-input--required"
                        :error="getError(`applicants.${index}.previousAddresses.${addressIndex}.dateMovedIn`)"
                        :error-messages="getErrorMessages(`applicants.${index}.previousAddresses.${addressIndex}.dateMovedIn`)"
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="details.previousAddresses[addressIndex]['dateMovedIn']"
                    :max="(new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)"
                    min="1900-01-01"
                ></v-date-picker>
            </v-menu>
            </div>

            <v-row class="mb-5 mt-2">
                <v-col cols="12" align="end">
                    <v-btn
                        color="primary"
                        outlined
                        @click.prevent="addPreviousAddress"
                        v-if="!formDisabled"
                    >
                        Add Previous Address
                    </v-btn>

                    <v-divider class="mt-3"></v-divider>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import {defineComponent, ref, onBeforeMount, computed, watch, nextTick} from '@vue/composition-api';
import HelperServices from "../../../services/HelperServices";
import {isEmpty, has, isNumber, forEach} from "lodash";

export default defineComponent({
    name: 'Details',
    props: [
        'accessToken',
        'title',
        'index',
        'getError',
        'getErrorMessages',
        'removeErrorMessage',
        'loggedInProfile',
        'value',
        'formDisabled'
    ],
    setup(props, { emit }) {
        const accessToken = ref(null);
        const updateKey = ref(0);
        const details = ref({
            email: null,
            title: null,
            firstName: null,
            middleNames: null,
            lastName: null,
            previousSurname: null,
            dateOfBirth: null,
            maritalStatus: null,
            gender: null,
            MODWorker: null,
            relationshipToPrimary: null,
            phone: null,
            homePhone: null,
            workPhone: null,
            mobilePhone: null,
            nationalInsuranceNumber: null,
            addressSameAsMainApplicant: false,
            livingCircumstance: null,
            homeAddressLine1: null,
            homeAddressLine2: null,
            homePostcode: null,
            homeLocalAuthorityId: null,
            homeTown: null,
            homeCounty: null,
            homeMoveInDate: null,
            previousAddresses: []
        });

        const dateOfBirthMenuRef = ref(null);
        const showDateOfBirthDatePicker = ref(false);
        const dateOfBirthActivePicker = ref(null);

        const homeMoveInDateMenuRef = ref(null);
        const showHomeMoveInDatePicker = ref(false);
        const homeMoveInActivePicker = ref(null);
        const genderChoices = ref([]);
        const livingCircumstanceChoices = ref([]);

        // Options
        const titleChoices = ref([
            { text: 'Mr',  value: 'Mr' },
            { text: 'Mrs',  value: 'Mrs' },
            { text: 'Miss',  value: 'Miss' },
            { text: 'Ms',  value: 'Ms' },
            { text: 'Mx',  value: 'Mx' },
            { text: 'Dr',  value: 'Dr' },
            { text: 'Sir',  value: 'Sir' },
            { text: 'Other',  value: 'Other' },
        ]);
        const maritalStatusChoices = ref([]);
        const localAuthorityChoices = ref([]);
        const countyChoices = ref([]);
        const relationshipToPrimaryChoices = ref([
            { text: 'Spouse',  value: 1 },
            { text: 'Partner',  value: 2 },
            { text: 'Friend',  value: 3 },
            { text: 'Sibling',  value: 4 },
            { text: 'Son',  value: 5 },
            { text: 'Daughter',  value: 6 },
            { text: 'Baby Due',  value: 7 },
            { text: 'Foster Child',  value: 8 },
            { text: 'Pregnant / Baby Due',  value: 9 }
        ]);

        const armedForcesMemberChoices = ref([
            { text: 'Yes',  value: 1 },
            { text: 'No',  value: 0 }
        ]);
        const eligibleCitizenChoices = ref([
            { text: 'Yes',  value: 1 },
            { text: 'No',  value: 0 }
        ]);
        const hasDisabilityChoices = ref([
            { text: 'Yes',  value: 1 },
            { text: 'No',  value: 0 }
        ]);

        const rules = ref({
            firstName : [
                v => !!v || 'First name is required',
            ],
            lastName : [
                v => !!v || 'Last name is required',
            ],
            title : [
                v => !!v || 'Title is required',
            ],
            phone : [
                v => !!v || 'Phone number is required',
                v => /^[0-9]*$/.test(v) || 'Please enter a valid phone number'
            ],
            homePhone : [
                v => (!v || /^(\s*|\d+)$/.test(v)) || 'Please enter a valid phone number'
            ],
            workPhone : [
                v => (!v || /^(\s*|\d+)$/.test(v)) || 'Please enter a valid phone number'
            ],
            mobilePhone : [
                v => (!v || /^(\s*|\d+)$/.test(v)) || 'Please enter a valid phone number'
            ],
            maritalStatus: [
                v => !!v || 'Marital status is required',
            ],
            gender: [
                v => !!v || 'Gender is required',
            ],
            relationshipToPrimary: [
                v => !!v || 'Relationship to primary is required',
            ],
            email : [
                v => !!v || 'E-mail is required',
                v => /.+@.+/.test(v) || 'E-mail must be valid',
            ],
            nationalInsuranceNumber: [
                v => !!v || 'National insurance number is required',
                v => /^(?!BG)(?!GB)(?!NK)(?!KN)(?!TN)(?!NT)(?!ZZ)(?:[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z])(?:\s*\d\s*){6}([A-D]|\s)$/.test(v) || 'National insurance number must be valid',
            ],
            homeAddressLine1: [
                v => !!v || 'Current home address line 1 is required',
            ],
            homeTown: [
                v => !!v || 'Current home town is required',
            ],
            homeCounty: [
                v => !!v || 'Current home county is required',
            ],
            homeLocalAuthority: [
                v => !!v || 'Current home local authority is required',
            ],
            homePostcode : [
                v => !!v || 'Home postcode is required',
                v => /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/.test(v) || 'Please enter a valid postcode'
            ],
            homeMoveInDate: [
                v => !!v || 'Current home move in date is required',
            ],
            livingCircumstance : [
                v => !!v || 'Current living situation is required',
            ],
            addressLine1: [
                v => !!v || 'Previous building name is required',
            ],
            previousTown: [
                v => !!v || 'Previous town is required',
            ],
            previousCounty: [
                v => !!v || 'Previous county is required',
            ],
            previousPostcode: [
                v => !!v || 'Previous postcode is required',
                v => /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/.test(v) || 'Please enter a valid postcode'
            ],
            previousMoveInDate: [
                v => !!v || 'Previous move in date is required',
                v => /(0?[1-9]|[12][0-9]|3[01])[/.](0?[1-9]|1[012])[/.](19|20)\d\d/.test(v) || 'Please enter a valid date (DD/MM/YYYY)'
            ]
        });

        const dateOfBirthFormatted = computed(() => {
            return formatDate(details.value.dateOfBirth);
        });

        const homeMoveInDateFormatted = computed(() => {
            return formatDate(details.value.homeMoveInDate);
        });

        watch(showDateOfBirthDatePicker, (currentValue) => {
            currentValue && setTimeout(() => (dateOfBirthActivePicker.value = 'YEAR'))
        });

        watch(showHomeMoveInDatePicker, (currentValue) => {
            currentValue && setTimeout(() => (homeMoveInActivePicker.value = 'YEAR'))
        });

        onBeforeMount(() => {
            init();
        });

        function previousAddressFormattedMoveInDate(dateValue){
            return formatDate(dateValue);
        };


        function saveDateOfBirthPicker(date) {
            dateOfBirthMenuRef.value.save(date);
        }

        function saveMoveInDatePicker(date) {
            homeMoveInDateMenuRef.value.save(date);
        }

        function formatDate (date) {
            if (!date) return null
            const [year, month, day] = date.split('-')
            return `${day}/${month}/${year}`;
        }

        function removePreviousAddress(index) {
            if(props.formDisabled === false) {
                details.value.previousAddresses.splice(index, 1);
            }
        }

        function addPreviousAddress() {
            if(props.formDisabled === false) {
                details.value.previousAddresses.push({
                    addressLine1: null,
                    addressLine2: null,
                    town: null,
                    county: null,
                    postcode: null,
                    dateMovedIn: null,
                })
            }
        }

        function setApplicantCurrentAddressFromMain(val) {
            if(val === true) {

            }
        }

        function fetchMaritalStatuses() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getMaritalStatuses()
                    .then(function (response) {
                        if(response.data.data) {
                            maritalStatusChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        console.error(error);
                        reject(error);
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        function searchPostcode(field, index = null) {
            let fieldRef = details.value[field];
            if(field == 'postcode' && isNumber(index)) {
                fieldRef = details.value.previousAddresses[index].postcode;
            }
            HelperServices.getLocalAuthorityFromPostcode(fieldRef)
                .then(function (response) {
                    if(has(response, 'data.id')) {
                        if(field == 'homePostcode') {
                            details.value['homeLocalAuthorityId'] = response.data.id;
                        } else {
                            details.value.previousAddresses[index].localAuthorityId = response.data.id;
                        }
                    }
                    if(has(response, 'data.name')) {
                        if(field == 'homePostcode') {
                            details.value.homeTown = response.data.name;
                        } else {
                            details.value.previousAddresses[index].town = response.data.name;
                        }
                    }
                    if(has(response, 'data.county.id')) {
                        if(field == 'homePostcode') {
                            details.value.homeCounty = response.data.county.id;
                        } else {
                            details.value.previousAddresses[index].county = response.data.county.id;
                        }
                    }
                    incrementUpdateKey();
                })
                .catch(function (error) {
                    console.error(error);
                })
        }

        function incrementUpdateKey() {
            updateKey.value = updateKey.value + 1;
        }

        function fetchLocalAuthorities() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getLocalAuthorities()
                    .then(function (response) {
                        if(response.data.data) {
                            localAuthorityChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        console.error(error);
                        reject(error);
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }
        function fetchCounties() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getCounties()
                    .then(function (response) {
                        if(response.data.data) {
                            countyChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        console.error(error);
                        reject(error);
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        function fetchGenderChoices() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getGenderChoices()
                    .then(function (response) {
                        if(response.data.data) {
                            genderChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error)
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        function fetchLivingCircumstances() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getLivingCircumstances()
                    .then(function (response) {
                        if(response.data.data) {
                            livingCircumstanceChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error)
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        function setFields() {
            let currentFields = {};
            forEach(details.value, function(detValue, detKey) {
                if(has(props, 'value.' + detKey)) {
                    currentFields[detKey] = props.value[detKey];
                } else {
                    currentFields[detKey] = detValue;
                }
            });

            if(props.index == 0 && props.loggedInProfile) {
                if(isEmpty(currentFields.email)) {
                    currentFields.email = props.loggedInProfile.email;
                }
                if(isEmpty(currentFields.firstName)) {
                    currentFields.firstName = props.loggedInProfile.firstName;
                }
                if(isEmpty(currentFields.lastName)) {
                    currentFields.lastName = props.loggedInProfile.lastName;
                }
                if(isEmpty(currentFields.phone)) {
                    currentFields.phone = props.loggedInProfile.phone;
                }
            }
            details.value = currentFields;
        }

        function init() {
            if(props.accessToken !== '') {
                accessToken.value = props.accessToken;
            }
            Promise
                .all([
                    fetchMaritalStatuses(),
                    fetchLocalAuthorities(),
                    fetchCounties(),
                    fetchGenderChoices(),
                    fetchLivingCircumstances(),
                ])
                .then(() => {
                    setFields();
                    emit("input", props.index, details.value);
                    emit('toggleLoading', false);
                });
        }


        watch(details, (newData, oldData) => {
            emit("input", props.index, newData);
        }, {
            deep: true
        });

        return {
            details,
            rules,
            dateOfBirthFormatted,
            homeMoveInDateFormatted,
            titleChoices,
            relationshipToPrimaryChoices,
            eligibleCitizenChoices,
            armedForcesMemberChoices,
            hasDisabilityChoices,
            maritalStatusChoices,
            showHomeMoveInDatePicker,
            showDateOfBirthDatePicker,
            dateOfBirthMenuRef,
            homeMoveInDateMenuRef,
            homeMoveInActivePicker,
            dateOfBirthActivePicker,
            localAuthorityChoices,
            countyChoices,
            updateKey,
            genderChoices,
            livingCircumstanceChoices,
            previousAddressFormattedMoveInDate,
            addPreviousAddress,
            removePreviousAddress,
            saveDateOfBirthPicker,
            saveMoveInDatePicker,
            searchPostcode,
            setApplicantCurrentAddressFromMain
        }
    },
})
</script>
